import { useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MessageSquarePlus, X } from "lucide-react";

const KINDS = [
  { v: "bug", label: "Something's broken" },
  { v: "idea", label: "An idea" },
  { v: "other", label: "Other" },
];

export function FeedbackButton() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("bug");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  if (!user || user === false) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/feedback", { kind, message, page: location.pathname });
      toast.success("Thank you — that helps us make this better.");
      setMessage("");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="feedback-open"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 left-5 z-40 inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-full bg-stone-900 text-white text-xs font-semibold shadow-lg hover:bg-amber-700 transition-colors"
      >
        <MessageSquarePlus className="w-4 h-4" /> <span className="hidden sm:inline">Beta feedback</span>
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="warm-card w-full max-w-md p-6 space-y-3" data-testid="feedback-form">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-stone-900">Tell us what you noticed</h3>
              <button type="button" onClick={() => setOpen(false)} data-testid="feedback-close" className="text-stone-400 hover:text-stone-700"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {KINDS.map((k) => (
                <button key={k.v} type="button" data-testid={`feedback-kind-${k.v}`} onClick={() => setKind(k.v)} className={`px-3 py-1 rounded-full text-xs font-semibold ${kind === k.v ? "bg-amber-600 text-white" : "bg-stone-100 text-stone-700"}`}>{k.label}</button>
              ))}
            </div>
            <textarea
              data-testid="feedback-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              required
              minLength={3}
              placeholder="What happened, or what would make this better? Screens, steps, anything helps."
              className="w-full px-3 py-2 rounded-xl border border-stone-300 text-sm focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none"
            />
            <div className="text-[11px] text-stone-500">Sent with your name and email so we can follow up. Please don't include other people's private details.</div>
            <button type="submit" data-testid="feedback-submit" disabled={busy} className="amber-btn w-full text-sm disabled:opacity-50">{busy ? "Sending…" : "Send feedback"}</button>
          </form>
        </div>
      )}
    </>
  );
}
